import { useEffect, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { tr } from "date-fns/locale";
import { Repeat, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DatePickerTR } from "@/components/ui/date-picker-tr";
import { computeRepeatDates, RecurringPreview, WeekdayPicker, newSeriesId } from "@/components/RecurringOptions";

const COUNTS = [1, 2, 3, 4, 5, 6, 7, 8, 10, 12, 16, 20, 24];

// Dates after the source reservation (never in the past)
const nextDates = ({ source, type, days, endMode, count, until }) => {
  const today = format(new Date(), "yyyy-MM-dd");
  const all = computeRepeatDates({ start: source, enabled: true, type, days: [...days, source.getDay()], endMode: "date", until: endMode === "date" ? until : format(addDays(source, 400), "yyyy-MM-dd") });
  const future = all.filter((d) => d > format(source, "yyyy-MM-dd") && d >= today);
  return endMode === "date" ? future : future.slice(0, count);
};

const PlanFields = ({ v, set, source, maxCount }) => (
  <div className="space-y-3">
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs">Tekrar</Label>
        <Select value={v.type} onValueChange={(x) => set({ type: x })}>
          <SelectTrigger className="rounded-lg h-9 mt-1" data-testid="repeat-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="weekly">Her hafta</SelectItem>
            <SelectItem value="daily">Her gün</SelectItem>
            <SelectItem value="weekdays">Haftanın belirli günleri</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Bitiş</Label>
        <Select value={v.endMode} onValueChange={(x) => set({ endMode: x })}>
          <SelectTrigger className="rounded-lg h-9 mt-1" data-testid="repeat-end-mode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="count">Ders sayısına göre</SelectItem>
            <SelectItem value="date">Bitiş tarihine göre</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
    {v.type === "weekdays" && <WeekdayPicker days={v.days} fixedDay={source.getDay()} onChange={(d) => set({ days: d })} />}
    {v.endMode === "count" ? (
      <div>
        <Label className="text-xs">Yeni ders sayısı</Label>
        <Select value={String(v.count)} onValueChange={(x) => set({ count: Number(x) })}>
          <SelectTrigger className="rounded-lg h-9 mt-1" data-testid="repeat-count-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COUNTS.filter((n) => maxCount == null || n <= Math.max(1, maxCount)).map((n) => <SelectItem key={n} value={String(n)}>{n} ders</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    ) : (
      <div>
        <Label className="text-xs">Son tarih</Label>
        <DatePickerTR value={v.until} onChange={(x) => set({ until: x })} returnDateString minDate={addDays(source, 1)} className="rounded-lg w-full mt-1" data-testid="repeat-until-date" />
      </div>
    )}
  </div>
);

export const RepeatReservationDialog = ({ reservation, member, businessId, defaultStatus, onClose, onDone }) => {
  const source = parseISO(reservation.date);
  const [v, setV] = useState({ type: "weekly", days: [], endMode: "count", count: 4, until: "" });
  const [remaining, setRemaining] = useState(null);
  const [status, setStatus] = useState({ bad: 0, overLimit: false, badDates: [], pending: true });
  const [saving, setSaving] = useState(false);
  const set = (p) => setV((s) => ({ ...s, ...p }));
  const isPackage = reservation.payment_type === "package" && reservation.member_package_id;
  const tpl = member?.recurring_template;

  useEffect(() => {
    if (!isPackage) return;
    fetch(`${API}/members/${reservation.member_id}/packages`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => {
        const mp = (list || []).find((p) => p.member_package_id === reservation.member_package_id);
        setRemaining(mp?.remaining_sessions ?? null);
      })
      .catch(() => {});
  }, [reservation.member_id, reservation.member_package_id, isPackage]);

  const dates = nextDates({ source, ...v });
  const slot = { startTime: reservation.start_time, endTime: reservation.end_time, trainerId: reservation.trainer_id, roomId: reservation.room_id, branch: reservation.branch, trainingType: reservation.training_type, memberId: reservation.member_id, businessId };
  const good = dates.filter((d) => !(status.badDates || []).includes(d));
  const canSave = good.length > 0 && !status.pending && !status.overLimit && !saving && (v.endMode === "count" || v.until);

  const save = async () => {
    setSaving(true);
    let ok = 0;
    const failed = [];
    const seriesId = reservation.series_id || newSeriesId();
    for (const date of good) {
      const body = {
        business_id: businessId, member_id: reservation.member_id, trainer_id: reservation.trainer_id, room_id: reservation.room_id,
        branch: reservation.branch, date, start_time: reservation.start_time, end_time: reservation.end_time,
        training_type: reservation.training_type, allow_shared: reservation.allow_shared !== false, status: defaultStatus,
        is_recurring: true, add_to_online: !!reservation.open_class_id, payment_type: reservation.payment_type || "package",
        member_package_id: isPackage ? reservation.member_package_id : null, package_id: isPackage ? reservation.member_package_id : null,
        card_type: reservation.payment_type === "card" ? (reservation.card_type || reservation.card_name) : null,
        is_demo: reservation.payment_type === "demo", series_id: seriesId, series_source_id: reservation.reservation_id,
      };
      const r = await fetch(`${API}/reservations/create`, { method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify(body) });
      if (r.ok) ok++;
      else failed.push(`${format(parseISO(date), "d MMM", { locale: tr })}: ${(await r.json().catch(() => ({}))).detail || "hata"}`);
    }
    setSaving(false);
    if (ok) toast.success(`${ok} yeni rezervasyon oluşturuldu`);
    if (failed.length) toast.error(`${failed.length} tarih oluşturulamadı — ${failed.slice(0, 3).join(" · ")}`, { duration: 8000 });
    if (ok) onDone();
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg md:max-h-[90vh] overflow-y-auto content-start" data-testid="repeat-reservation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-navy"><Repeat className="w-5 h-5" /> Rezervasyonu Tekrarla</DialogTitle>
        </DialogHeader>
        <div className="rounded-xl bg-slate-50 p-3 text-sm space-y-0.5" data-testid="repeat-source-summary">
          <p className="font-semibold">{reservation.member_name}</p>
          <p className="text-grey">{format(source, "d MMMM yyyy EEEE", { locale: tr })} · {reservation.start_time}–{reservation.end_time}</p>
          <p className="text-grey">{reservation.trainer_name || ""} · {reservation.branch || ""}{reservation.open_class_id ? " · Online" : ""}</p>
          {remaining != null && <p className="text-xs text-purple-700 font-medium">Pakette kalan hak: {remaining}</p>}
        </div>
        {tpl?.days?.length > 0 && (
          <button type="button" className="text-xs font-semibold text-purple-700 underline text-left" onClick={() => set({ type: "weekdays", days: tpl.days.filter((d) => d !== source.getDay()) })} data-testid="repeat-apply-template">
            Kayıtlı düzeni uygula
          </button>
        )}
        <PlanFields v={v} set={set} source={source} maxCount={remaining} />
        {dates.length === 0 ? (
          <p className="text-xs text-red-600" data-testid="repeat-no-dates">Seçimlere göre oluşacak yeni tarih yok.</p>
        ) : (
          <div className="rounded-xl bg-purple-50 border border-purple-200 p-3">
            <RecurringPreview dates={dates} slot={slot} maxAllowed={remaining} onStatus={setStatus} minDates={1} />
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1 rounded-xl" onClick={onClose} data-testid="repeat-cancel-btn">Vazgeç</Button>
          <Button className="flex-1 rounded-xl bg-navy hover:bg-navy/90" disabled={!canSave} onClick={save} data-testid="repeat-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `${good.length} rezervasyon oluştur`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
